import { useState } from 'react';
import {
  Center,
  VStack,
  HStack,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Text,
  Input,
  useColorModeValue,
  Grid,
  GridItem,
  useToast,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/Modal';
import Steps, { Step } from 'components/ui/Steps';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface EpisodeData {
  anamnesis: {
    motivo: string;
    evolucion: string;
    sintomas: string;
  };
  signosVitales: {
    peso: number | '';
    talla: number | '';
    temperatura: number | '';
    presionArterial: string;
    frecuenciaCardiaca: number | '';
    frecuenciaRespiratoria: number | '';
  };
  impressionDiagnostica: string;
}

const EPISODE_STEPS: Step[] = [
  { id: '1', title: 'Anamnesis' },
  { id: '2', title: 'Signos Vitales' },
  { id: '3', title: 'Impresión Diagnóstica' },
];

const SIZES: ModalSize[] = ['sm', 'md', 'lg', 'xl', '2xl'];

const INITIAL_EPISODE_DATA: EpisodeData = {
  anamnesis: {
    motivo: '',
    evolucion: '',
    sintomas: '',
  },
  signosVitales: {
    peso: '',
    talla: '',
    temperatura: '',
    presionArterial: '',
    frecuenciaCardiaca: '',
    frecuenciaRespiratoria: '',
  },
  impressionDiagnostica: '',
};

export default function ModalEpisodioPreview() {
  const [openSize, setOpenSize] = useState<ModalSize | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [episodeData, setEpisodeData] = useState<EpisodeData>(INITIAL_EPISODE_DATA);
  const bg = useColorModeValue('gray.50', 'gray.900');
  const toast = useToast();

  const handleAnamnesisChange = (field: keyof EpisodeData['anamnesis'], value: string) => {
    setEpisodeData({
      ...episodeData,
      anamnesis: {
        ...episodeData.anamnesis,
        [field]: value,
      },
    });
  };

  const handleSignosVitalesChange = (
    field: keyof EpisodeData['signosVitales'],
    value: string | number
  ) => {
    setEpisodeData({
      ...episodeData,
      signosVitales: {
        ...episodeData.signosVitales,
        [field]: field === 'presionArterial' ? value : value === '' ? '' : Number(value),
      },
    });
  };

  const handleNextStep = () => {
    if (currentStep < EPISODE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateEpisode = () => {
    console.log('Nuevo Episodio Clínico:', episodeData);
    toast({
      title: 'Episodio creado',
      description: 'El episodio clínico ha sido creado exitosamente',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    resetModal();
  };

  const resetModal = () => {
    setOpenSize(null);
    setCurrentStep(0);
    setEpisodeData(INITIAL_EPISODE_DATA);
  };

  const isLastStep = currentStep === EPISODE_STEPS.length - 1;

  return (
    <Center minH='100vh' bg={bg}>
      <VStack spacing='6'>
        <Text fontSize='xl' fontWeight='700'>Modal Episodio — Preview</Text>

        <HStack spacing='3' flexWrap='wrap' justify='center'>
          {SIZES.map((s) => (
            <Button key={s} size='sm' colorScheme='brand' onClick={() => setOpenSize(s)}>
              {s.toUpperCase()}
            </Button>
          ))}
        </HStack>
      </VStack>

      <CustomModal
        isOpen={openSize !== null}
        onClose={resetModal}
        title='Nuevo Episodio Clínico'
        size={openSize ?? 'md'}
        primaryBtnLabel={isLastStep ? 'Crear Episodio' : 'Siguiente'}
        onPrimaryClick={isLastStep ? handleCreateEpisode : handleNextStep}
        headerExtra={
          <Steps steps={EPISODE_STEPS} activeStep={currentStep} size='sm' labelPlacement='bottom' />
        }
      >
        <VStack spacing='20px' align='stretch'>
          {/* STEP 1: ANAMNESIS */}
          {currentStep === 0 && (
            <>
              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Motivo de Consulta</FormLabel>
                <Textarea
                  placeholder='Describe el motivo principal de consulta...'
                  rows={3}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.anamnesis.motivo}
                  onChange={(e) => handleAnamnesisChange('motivo', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Evolución</FormLabel>
                <Textarea
                  placeholder='Describe la evolución del problema (ej: tiempo desde el inicio)...'
                  rows={3}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.anamnesis.evolucion}
                  onChange={(e) => handleAnamnesisChange('evolucion', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Síntomas</FormLabel>
                <Textarea
                  placeholder='Describe los síntomas principales y su intensidad (ej: EVA)...'
                  rows={3}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.anamnesis.sintomas}
                  onChange={(e) => handleAnamnesisChange('sintomas', e.target.value)}
                />
              </FormControl>
            </>
          )}

          {/* STEP 2: SIGNOS VITALES */}
          {currentStep === 1 && (
            <>
              <Grid templateColumns='repeat(2, 1fr)' gap='4'>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Peso (kg)</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 74.5'
                      fontSize='sm'
                      step='0.1'
                      value={episodeData.signosVitales.peso}
                      onChange={(e) => handleSignosVitalesChange('peso', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Talla (m)</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 1.72'
                      fontSize='sm'
                      step='0.01'
                      value={episodeData.signosVitales.talla}
                      onChange={(e) => handleSignosVitalesChange('talla', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Temperatura (°C)</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 36.6'
                      fontSize='sm'
                      step='0.1'
                      value={episodeData.signosVitales.temperatura}
                      onChange={(e) => handleSignosVitalesChange('temperatura', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Presión Arterial</FormLabel>
                    <Input
                      type='text'
                      placeholder='Ej: 120/80'
                      fontSize='sm'
                      value={episodeData.signosVitales.presionArterial}
                      onChange={(e) => handleSignosVitalesChange('presionArterial', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Frecuencia Cardíaca</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 72'
                      fontSize='sm'
                      value={episodeData.signosVitales.frecuenciaCardiaca}
                      onChange={(e) => handleSignosVitalesChange('frecuenciaCardiaca', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Frecuencia Respiratoria</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 16'
                      fontSize='sm'
                      value={episodeData.signosVitales.frecuenciaRespiratoria}
                      onChange={(e) => handleSignosVitalesChange('frecuenciaRespiratoria', e.target.value)}
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </>
          )}

          {/* STEP 3: IMPRESION DIAGNOSTICA */}
          {currentStep === 2 && (
            <>
              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Impresión Diagnóstica</FormLabel>
                <Textarea
                  placeholder='Describe la impresión diagnóstica basado en la evaluación...'
                  rows={6}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.impressionDiagnostica}
                  onChange={(e) =>
                    setEpisodeData({
                      ...episodeData,
                      impressionDiagnostica: e.target.value,
                    })
                  }
                />
              </FormControl>
            </>
          )}

          {/* NAVIGATION BUTTONS */}
          <HStack spacing='3' justify='space-between' pt='4'>
            <Button
              size='sm'
              variant='ghost'
              onClick={handlePrevStep}
              isDisabled={currentStep === 0}
            >
              Anterior
            </Button>
            <Text fontSize='xs' color='gray.500'>
              Paso {currentStep + 1} de {EPISODE_STEPS.length}
            </Text>
          </HStack>
        </VStack>
      </CustomModal>
    </Center>
  );
}
